import React, { useState } from 'react';
import Card from '../../components/ui/Card';
import { CalendarIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon, BarChart3Icon, PlayCircleIcon } from 'lucide-react';
const mockAggregators = [{
  id: 'agg1',
  name: 'Global Payments Network'
}, {
  id: 'agg2',
  name: 'Metro Financial Services'
}, {
  id: 'agg3',
  name: 'Digital Commerce Hub'
}, {
  id: 'agg4',
  name: 'PayTech Solutions'
}];
const mockMerchants = [{
  id: 'mer1',
  name: 'Global Electronics',
  aggregatorId: 'agg1'
}, {
  id: 'mer2',
  name: 'Fashion Boutique',
  aggregatorId: 'agg1'
}, {
  id: 'mer3',
  name: 'Tech Store',
  aggregatorId: 'agg2'
}, {
  id: 'mer4',
  name: 'Food Court',
  aggregatorId: 'agg2'
}, {
  id: 'mer5',
  name: 'Book Shop',
  aggregatorId: 'agg3'
}, {
  id: 'mer6',
  name: 'Sports Gear',
  aggregatorId: 'agg3'
}];
const generateTransactionSummary = () => {
  const summary = [];
  mockMerchants.forEach(merchant => {
    const totalTransactions = Math.floor(Math.random() * 100) + 20;
    const successfulTransactions = Math.floor(totalTransactions * 0.85);
    const failedTransactions = Math.floor(totalTransactions * 0.1);
    const pendingTransactions = totalTransactions - successfulTransactions - failedTransactions;
    const totalAmount = (Math.random() * 100000 + 10000).toFixed(2);
    summary.push({
      merchantId: merchant.id,
      merchantName: merchant.name,
      aggregatorId: merchant.aggregatorId,
      totalTransactions,
      successfulTransactions,
      failedTransactions,
      pendingTransactions,
      totalAmount: parseFloat(totalAmount),
      avgTransactionAmount: parseFloat((parseFloat(totalAmount) / totalTransactions).toFixed(2))
    });
  });
  return summary;
};
const TransactionSummaryReport: React.FC = () => {
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedAggregator, setSelectedAggregator] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [transactionSummary, setTransactionSummary] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportGenerated, setReportGenerated] = useState(false);
  const itemsPerPage = 10;
  const filteredMerchants = selectedAggregator ? mockMerchants.filter(m => m.aggregatorId === selectedAggregator) : mockMerchants;
  const generateReport = () => {
    let summaryFiltered = generateTransactionSummary();
    if (selectedAggregator) {
      summaryFiltered = summaryFiltered.filter(s => s.aggregatorId === selectedAggregator);
    }
    if (selectedMerchant) {
      summaryFiltered = summaryFiltered.filter(s => s.merchantId === selectedMerchant);
    }
    setTransactionSummary(summaryFiltered);
    setCurrentPage(1);
    setReportGenerated(true);
  };
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return transactionSummary.slice(startIndex, endIndex);
  };
  const getTotalPages = () => {
    return Math.ceil(transactionSummary.length / itemsPerPage);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2
    }).format(amount);
  };
  const resetFilters = () => {
    const date = new Date();
    date.setDate(1);
    setFromDate(date.toISOString().split('T')[0]);
    setToDate(new Date().toISOString().split('T')[0]);
    setSelectedAggregator('');
    setSelectedMerchant('');
    setTransactionSummary([]);
    setReportGenerated(false);
  };
  return <div className="space-y-6" data-id="element-6582">
      <div data-id="element-6583">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-6584">
          Transaction Summary Report
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-6585">
          View aggregated transaction data grouped by merchants
        </p>
      </div>
      <Card data-id="element-6586">
        <div className="flex items-center justify-between mb-4" data-id="element-6587">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-6588">
            Filters
          </h3>
          <button onClick={resetFilters} className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-6589">
            Reset Filters
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-6590">
          <div data-id="element-6591">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6592">
              From Date
            </label>
            <div className="relative" data-id="element-6593">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6594">
                <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-6595" />
              </div>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6596" />
            </div>
          </div>
          <div data-id="element-6597">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6598">
              To Date
            </label>
            <div className="relative" data-id="element-6599">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6600">
                <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-6601" />
              </div>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6602" />
            </div>
          </div>
          <div data-id="element-6603">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6604">
              Aggregator
            </label>
            <select value={selectedAggregator} onChange={e => {
            setSelectedAggregator(e.target.value);
            setSelectedMerchant('');
          }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6605">
              <option value="" data-id="element-6606">All Aggregators</option>
              {mockAggregators.map(agg => <option key={agg.id} value={agg.id} data-id="element-6607">
                  {agg.name}
                </option>)}
            </select>
          </div>
          <div data-id="element-6608">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6609">
              Merchant
            </label>
            <select value={selectedMerchant} onChange={e => setSelectedMerchant(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-6610">
              <option value="" data-id="element-6611">All Merchants</option>
              {filteredMerchants.map(merchant => <option key={merchant.id} value={merchant.id} data-id="element-6612">
                  {merchant.name}
                </option>)}
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end" data-id="element-6613">
          <button onClick={generateReport} className="inline-flex items-center px-6 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-6614">
            <PlayCircleIcon className="h-5 w-5 mr-2" data-id="element-6615" />
            Generate Report
          </button>
        </div>
      </Card>
      <Card data-id="element-6616">
        <div className="flex items-center justify-between mb-4" data-id="element-6617">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-6618">
            Transaction Summary
          </h3>
          {reportGenerated && transactionSummary.length > 0 && <button onClick={() => console.log('Export')} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6619">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-6620" />
              Export
            </button>}
        </div>
        {!reportGenerated ? <div className="text-center py-12" data-id="element-6621">
            <BarChart3Icon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6622" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-6623">
              No Report Generated
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-6624">
              Select your filters above and click "Generate Report" to view the
              transaction summary.
            </p>
          </div> : transactionSummary.length === 0 ? <div className="text-center py-12" data-id="element-6625">
            <BarChart3Icon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6626" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-6627">
              No Data Found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-6628">
              No summary data matches your selected filters. Try adjusting your
              criteria.
            </p>
          </div> : <>
            <div className="overflow-x-auto" data-id="element-6629">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6630">
                <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-6631">
                  <tr data-id="element-6632">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6633">
                      Merchant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6634">
                      Total Transactions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6635">
                      Successful
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6636">
                      Failed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6637">
                      Pending
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6638">
                      Total Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-6639">
                      Avg Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6640">
                  {getCurrentPageData().map((summary: any) => <tr key={summary.merchantId} data-id="element-6641">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-6642">
                        {summary.merchantName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6643">
                        {summary.totalTransactions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400" data-id="element-6644">
                        {summary.successfulTransactions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400" data-id="element-6645">
                        {summary.failedTransactions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 dark:text-yellow-400" data-id="element-6646">
                        {summary.pendingTransactions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-6647">
                        {formatCurrency(summary.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6648">
                        {formatCurrency(summary.avgTransactionAmount)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {getTotalPages() > 1 && <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-4 py-3 sm:px-6 mt-4" data-id="element-6649">
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between" data-id="element-6650">
                  <div data-id="element-6651">
                    <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-6652">
                      Showing{' '}
                      <span className="font-medium" data-id="element-6653">
                        {(currentPage - 1) * itemsPerPage + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium" data-id="element-6654">
                        {Math.min(currentPage * itemsPerPage, transactionSummary.length)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium" data-id="element-6655">
                        {transactionSummary.length}
                      </span>{' '}
                      results
                    </p>
                  </div>
                  <div data-id="element-6656">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" data-id="element-6657">
                      <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-6658">
                        <ChevronLeftIcon className="h-5 w-5" data-id="element-6659" />
                      </button>
                      {Array.from({
                  length: getTotalPages()
                }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-6660">
                          {page}
                        </button>)}
                      <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages()))} disabled={currentPage === getTotalPages()} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-6661">
                        <ChevronRightIcon className="h-5 w-5" data-id="element-6662" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>}
          </>}
      </Card>
    </div>;
};
export default TransactionSummaryReport;